import { useState, useEffect, useCallback } from 'react';
import TopNav from './TopNav';
import { useToast } from '../Toast';
import WheelCountCell from '../WheelCountCell';

const API = process.env.REACT_APP_API_URL || '';

// Coffee Inventory — daily / weekly / monthly counts by location. No pars; pure
// tracking. Item catalog + each item's count frequency are managed in-tool.
// Mirrors the Spirits Inventory count model (per-period upsert, autosave cells).

const LOCATIONS = [
  { key: 'northside', label: 'Northside' },
  { key: 'midtown',   label: 'Midtown' },
];

const FREQ_TABS = [
  { key: 'daily',   label: 'Daily' },
  { key: 'weekly',  label: 'Weekly' },
  { key: 'monthly', label: 'Monthly' },
];

// Section display order + labels. Keep the keys in sync with coffee_inventory.sql.
const CATEGORIES = [
  { key: 'coffee_unopened',     label: 'Coffee — Unopened' },
  { key: 'coffee_retail',       label: 'Retail Bags' },
  { key: 'coffee_guest_retail', label: 'Guest Retail' },
  { key: 'milk',                label: 'Milk' },
  { key: 'tea',                 label: 'Tea' },
  { key: 'cold_brew_kegs',      label: 'Cold Brew Kegs' },
  { key: 'syrup',               label: 'Syrups' },
  { key: 'syrup_seasonal',      label: 'Seasonal Syrups' },
  { key: 'supplies',            label: 'Supplies' },
  { key: 'cleaning',            label: 'Cleaning Supplies' },
];
const CAT_LABEL = Object.fromEntries(CATEGORIES.map(c => [c.key, c.label]));
const CAT_INDEX = Object.fromEntries(CATEGORIES.map((c, i) => [c.key, i]));

const FREQ_LABEL = { daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly' };
const FREQ_BADGE = {
  daily:   'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
  weekly:  'bg-sky-500/20 text-sky-300 border-sky-500/30',
  monthly: 'bg-violet-500/20 text-violet-300 border-violet-500/30',
};

// 'YYYY-MM-DD' → Date at local noon (avoids TZ off-by-one).
function parseDate(s) { return s ? new Date(`${s}T12:00:00`) : null; }

function periodLabel(frequency, periodStart) {
  const d = parseDate(periodStart);
  if (!d) return '';
  if (frequency === 'daily') {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const isToday = d.toDateString() === today.toDateString();
    return `${isToday ? 'Today · ' : ''}${d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}`;
  }
  if (frequency === 'weekly') {
    return `Week of ${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
  }
  return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }); // monthly
}

function fmtQty(n) {
  const v = parseFloat(n);
  if (!isFinite(v)) return '';
  return Number.isInteger(v) ? String(v) : String(v);
}

// Count entry is the shared WheelCountCell (wheel on mobile, input on desktop).
// Coffee counts allow quarter steps (0.25 / 0.5).

// ── Count view for one cadence ────────────────────────────────────────────────
function CountView({ location, frequency }) {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});      // item_id -> { qty, submitted_by_name }
  const [periodStart, setPeriodStart] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [itemsRes, countsRes] = await Promise.all([
        fetch(`${API}/api/coffee-inventory/items?frequency=${frequency}`, { credentials: 'include' }),
        fetch(`${API}/api/coffee-inventory/counts?location=${location}&frequency=${frequency}`, { credentials: 'include' }),
      ]);
      const itemsData = itemsRes.ok ? await itemsRes.json() : [];
      const countsData = countsRes.ok ? await countsRes.json() : { counts: [] };
      setItems(Array.isArray(itemsData) ? itemsData : []);
      const map = {};
      (countsData.counts || []).forEach(c => { map[c.item_id] = c; });
      setCounts(map);
      setPeriodStart(countsData.period_start || null);
    } catch {
      toast.error('Failed to load counts.');
    } finally {
      setLoading(false);
    }
  }, [location, frequency, toast]);

  useEffect(() => { load(); }, [load]);

  const saveCount = async (item, qty) => {
    try {
      const res = await fetch(`${API}/api/coffee-inventory/counts`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ item_id: item.id, location, frequency, period_start: periodStart, qty }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || 'Could not save count.');
      setCounts(prev => ({ ...prev, [item.id]: { ...(prev[item.id] || {}), item_id: item.id, qty } }));
    } catch (e) {
      toast.error(e.message);
      throw e;
    }
  };

  if (loading) return <p className="text-gray-500 text-sm text-center py-12">Loading…</p>;

  const counted = items.filter(i => counts[i.id] !== undefined).length;
  const total = items.length;
  const complete = total > 0 && counted >= total;

  // Group items by category, preserving the CATEGORIES order.
  const groups = [];
  const byCat = {};
  items.forEach(i => { (byCat[i.category] = byCat[i.category] || []).push(i); });
  Object.keys(byCat)
    .sort((a, b) => (CAT_INDEX[a] ?? 99) - (CAT_INDEX[b] ?? 99))
    .forEach(cat => groups.push({ cat, items: byCat[cat] }));

  return (
    <div>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-5">
        <div className="text-gray-400 text-sm">
          <span className="text-white font-semibold">{FREQ_LABEL[frequency]} count</span>
          {periodStart && <span className="ml-2">· {periodLabel(frequency, periodStart)}</span>}
        </div>
        <span className={`self-start sm:self-auto text-xs font-semibold px-3 py-1 rounded-full border ${
          complete ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
                   : 'bg-gray-700/60 text-gray-300 border-gray-600'
        }`}>
          {counted} / {total} counted
        </span>
      </div>

      {total === 0 ? (
        <p className="text-gray-500 text-sm text-center py-12">
          No items assigned to the {FREQ_LABEL[frequency].toLowerCase()} count yet.
        </p>
      ) : (
        <div className="space-y-6">
          {groups.map(({ cat, items: catItems }) => (
            <div key={cat}>
              <h3 className="text-cream text-sm font-bold uppercase tracking-wider mb-2">{CAT_LABEL[cat] || cat}</h3>
              <div className="bg-gray-800 rounded-xl border border-gray-700 divide-y divide-gray-700/70">
                {catItems.map(item => {
                  const c = counts[item.id];
                  return (
                    <div key={item.id} className="flex items-center justify-between gap-3 px-3 sm:px-4 py-2.5">
                      <div className="min-w-0">
                        <div className="text-white text-sm truncate">{item.name}</div>
                        <div className="text-gray-500 text-xs">
                          {item.unit ? item.unit : ''}
                          {c?.submitted_by_name ? `${item.unit ? ' · ' : ''}by ${c.submitted_by_name}` : ''}
                        </div>
                      </div>
                      <WheelCountCell value={c?.qty} onSave={(q) => saveCount(item, q)}
                        label={item.name} itemName={CAT_LABEL[cat] || cat} allowDecimal step={0.25}
                        desktopInputClassName="w-20 bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-right text-sm focus:outline-none focus:border-orange-500" />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── History view ──────────────────────────────────────────────────────────────
function HistoryView({ location }) {
  const toast = useToast();
  const [freq, setFreq] = useState('weekly');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null); // { period_start, items }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/coffee-inventory/history?location=${location}&frequency=${freq}`, { credentials: 'include' });
      setRows(res.ok ? await res.json() : []);
    } catch {
      toast.error('Failed to load history.');
    } finally {
      setLoading(false);
    }
  }, [location, freq, toast]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (period_start) => {
    try {
      const res = await fetch(`${API}/api/coffee-inventory/history/detail?location=${location}&frequency=${freq}&period_start=${period_start}`, { credentials: 'include' });
      const items = res.ok ? await res.json() : [];
      setDetail({ period_start, items });
    } catch {
      toast.error('Failed to load that count.');
    }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-5">
        {FREQ_TABS.map(f => (
          <button key={f.key} onClick={() => setFreq(f.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              freq === f.key ? 'bg-orange-500 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'
            }`}>
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-gray-500 text-sm text-center py-12">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-gray-500 text-sm text-center py-12">No {FREQ_LABEL[freq].toLowerCase()} counts recorded yet.</p>
      ) : (
        <div className="bg-gray-800 rounded-xl border border-gray-700 divide-y divide-gray-700/70">
          {rows.map(r => (
            <button key={r.period_start} onClick={() => openDetail(r.period_start)}
              className="w-full flex items-center justify-between gap-3 px-3 sm:px-4 py-3 text-left hover:bg-gray-700/30 transition">
              <div className="min-w-0">
                <div className="text-white text-sm font-medium">{periodLabel(freq, r.period_start)}</div>
                <div className="text-gray-500 text-xs truncate">
                  {r.counted} item{r.counted === 1 ? '' : 's'}
                  {r.contributors ? ` · ${r.contributors}` : ''}
                </div>
              </div>
              <span className="text-gray-500 text-lg flex-shrink-0">›</span>
            </button>
          ))}
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setDetail(null)}>
          <div className="bg-gray-800 rounded-xl border border-gray-700 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700 sticky top-0 bg-gray-800">
              <h3 className="text-white font-semibold">{periodLabel(freq, detail.period_start)}</h3>
              <button onClick={() => setDetail(null)} className="text-gray-400 hover:text-white text-2xl leading-none">×</button>
            </div>
            <div className="divide-y divide-gray-700/70">
              {detail.items.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-8">No items counted.</p>
              ) : detail.items.map((it, i) => (
                <div key={i} className="flex items-center justify-between gap-3 px-4 py-2.5">
                  <div className="min-w-0">
                    <div className="text-white text-sm truncate">{it.name}</div>
                    <div className="text-gray-500 text-xs">{CAT_LABEL[it.category] || it.category}{it.submitted_by_name ? ` · ${it.submitted_by_name}` : ''}</div>
                  </div>
                  <span className="text-white text-sm font-semibold flex-shrink-0">{fmtQty(it.qty)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Manage view (item catalog) ────────────────────────────────────────────────
function ManageView() {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // item | 'new' | null

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/coffee-inventory/items?includeHidden=1`, { credentials: 'include' });
      setItems(res.ok ? await res.json() : []);
    } catch {
      toast.error('Failed to load items.');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const move = async (index, dir) => {
    const next = index + dir;
    if (next < 0 || next >= items.length) return;
    const reordered = [...items];
    [reordered[index], reordered[next]] = [reordered[next], reordered[index]];
    setItems(reordered);
    try {
      const res = await fetch(`${API}/api/coffee-inventory/items/reorder`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ orderedIds: reordered.map(i => i.id) }),
      });
      if (!res.ok) throw new Error();
    } catch { toast.error('Reorder failed.'); load(); }
  };

  const toggleHidden = async (item) => {
    try {
      const res = await fetch(`${API}/api/coffee-inventory/items/${item.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ hidden: !item.hidden }),
      });
      if (!res.ok) throw new Error();
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, hidden: !item.hidden } : i));
    } catch { toast.error('Could not update item.'); }
  };

  const del = async (item) => {
    if (!window.confirm(`Delete "${item.name}"? This removes its count history too.`)) return;
    try {
      const res = await fetch(`${API}/api/coffee-inventory/items/${item.id}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new Error();
      setItems(prev => prev.filter(i => i.id !== item.id));
      toast.success('Item deleted.');
    } catch { toast.error('Delete failed.'); }
  };

  if (loading) return <p className="text-gray-500 text-sm text-center py-12">Loading…</p>;

  return (
    <div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-5">
        <p className="text-gray-400 text-sm">{items.length} items. Set each item's count frequency here.</p>
        <button onClick={() => setEditing('new')}
          className="px-4 py-2 rounded-lg text-white text-sm font-semibold shrink-0 self-start"
          style={{ backgroundColor: '#F05A28' }}>
          + Add Item
        </button>
      </div>

      <div className="bg-gray-800 rounded-xl border border-gray-700 divide-y divide-gray-700/70">
        {items.map((item, i) => (
          <div key={item.id} className={`flex items-center gap-2 px-3 sm:px-4 py-2.5 ${item.hidden ? 'opacity-50' : ''}`}>
            <div className="flex flex-col flex-shrink-0">
              <button onClick={() => move(i, -1)} disabled={i === 0}
                className="text-gray-500 hover:text-white disabled:opacity-20 text-xs leading-none">▲</button>
              <button onClick={() => move(i, 1)} disabled={i === items.length - 1}
                className="text-gray-500 hover:text-white disabled:opacity-20 text-xs leading-none">▼</button>
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-white text-sm truncate">
                {item.name}{item.unit ? <span className="text-gray-500"> · {item.unit}</span> : ''}
              </div>
              <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                <span className="text-xs text-gray-400">{CAT_LABEL[item.category] || item.category}</span>
                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${FREQ_BADGE[item.frequency]}`}>
                  {FREQ_LABEL[item.frequency]}
                </span>
                {item.hidden && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-700 text-gray-400">Hidden</span>}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-end flex-shrink-0">
              <button onClick={() => toggleHidden(item)} className="text-xs text-gray-400 hover:text-white transition">
                {item.hidden ? 'Show' : 'Hide'}
              </button>
              <button onClick={() => setEditing(item)} className="text-xs text-orange-400 hover:text-orange-300 transition">Edit</button>
              <button onClick={() => del(item)} className="text-xs text-red-500 hover:text-red-400 transition">Delete</button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <ItemModal
          item={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function ItemModal({ item, onClose, onSaved }) {
  const toast = useToast();
  const [name, setName] = useState(item?.name || '');
  const [category, setCategory] = useState(item?.category || CATEGORIES[0].key);
  const [unit, setUnit] = useState(item?.unit || '');
  const [frequency, setFrequency] = useState(item?.frequency || 'weekly');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) { toast.error('Name is required.'); return; }
    setSaving(true);
    try {
      const url = item ? `${API}/api/coffee-inventory/items/${item.id}` : `${API}/api/coffee-inventory/items`;
      const res = await fetch(url, {
        method: item ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ name: name.trim(), category, unit: unit.trim(), frequency }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || 'Could not save item.');
      toast.success(item ? 'Item updated.' : 'Item added.');
      onSaved();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-800 rounded-xl border border-gray-700 w-full max-w-md max-h-[90vh] overflow-y-auto p-6 space-y-4" onClick={e => e.stopPropagation()}>
        <h3 className="text-white font-semibold text-lg">{item ? 'Edit Item' : 'Add Item'}</h3>
        <div>
          <label className="block text-gray-400 text-xs font-semibold uppercase tracking-wider mb-1">Name</label>
          <input value={name} onChange={e => setName(e.target.value)} autoFocus
            className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:border-orange-500 focus:outline-none" />
        </div>
        <div>
          <label className="block text-gray-400 text-xs font-semibold uppercase tracking-wider mb-1">Section</label>
          <select value={category} onChange={e => setCategory(e.target.value)}
            className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:border-orange-500 focus:outline-none">
            {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-400 text-xs font-semibold uppercase tracking-wider mb-1">Count frequency</label>
            <select value={frequency} onChange={e => setFrequency(e.target.value)}
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:border-orange-500 focus:outline-none">
              {FREQ_TABS.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-gray-400 text-xs font-semibold uppercase tracking-wider mb-1">Unit <span className="normal-case text-gray-600">(optional)</span></label>
            <input value={unit} onChange={e => setUnit(e.target.value)} placeholder="case, bottle, 5lb…"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:border-orange-500 focus:outline-none" />
          </div>
        </div>
        <div className="flex gap-2 justify-end pt-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-300 hover:text-white transition">Cancel</button>
          <button onClick={save} disabled={saving}
            className="px-4 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-50" style={{ backgroundColor: '#F05A28' }}>
            {saving ? 'Saving…' : (item ? 'Save' : 'Add')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Location landing ──────────────────────────────────────────────────────────
function LocationLanding({ onSelect }) {
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
      <h2 className="text-cream text-2xl sm:text-3xl font-bold mb-1">Coffee Inventory</h2>
      <p className="text-gray-400 text-sm mb-8">Pick a location to start a count.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {LOCATIONS.map(l => (
          <button key={l.key} onClick={() => onSelect(l.key)}
            className="bg-gray-800 border border-gray-700 rounded-xl p-6 text-left hover:border-orange-500 hover:bg-gray-800/70 transition">
            <div className="text-white text-lg font-semibold">{l.label}</div>
            <div className="text-gray-500 text-sm mt-1">Daily · Weekly · Monthly counts</div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Root ──────────────────────────────────────────────────────────────────────
export default function CoffeeInventory({ user, canUpload, onBack }) {
  const [location, setLocation] = useState(null);
  const [tab, setTab] = useState('daily');

  const tabs = [
    ...FREQ_TABS,
    { key: 'history', label: 'History' },
    ...(canUpload ? [{ key: 'manage', label: 'Manage' }] : []),
  ];

  const locationLabel = LOCATIONS.find(l => l.key === location)?.label || '';

  if (!location) {
    return (
      <div className="min-h-screen bg-gray-900">
        <TopNav />
        <LocationLanding onSelect={setLocation} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <TopNav>
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-cream font-bold text-base sm:text-xl truncate">{locationLabel} Coffee Inventory</h1>
          <button onClick={() => { setLocation(null); setTab('daily'); }}
            className="text-sm text-gray-400 hover:text-white transition flex-shrink-0">← Locations</button>
        </div>
      </TopNav>

      <div className="border-b border-gray-700 px-4 sm:px-6">
        <div className="flex flex-wrap gap-4 sm:gap-6 max-w-4xl mx-auto">
          {tabs.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`py-3 text-sm font-medium whitespace-nowrap border-b-2 transition ${
                tab === t.key ? 'border-orange-500 text-white' : 'border-transparent text-gray-400 hover:text-gray-200'
              }`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="px-4 sm:px-6 py-6 max-w-4xl mx-auto">
        {(tab === 'daily' || tab === 'weekly' || tab === 'monthly') && (
          <CountView key={`${location}-${tab}`} location={location} frequency={tab} />
        )}
        {tab === 'history' && <HistoryView key={location} location={location} />}
        {tab === 'manage' && canUpload && <ManageView />}
      </div>
    </div>
  );
}
